// @ts-nocheck
import { useMemo } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Title,
  SimpleGrid,
  Table,
  Button,
  Badge,
  Divider,
} from '@mantine/core';
import { IconDownload, IconReceipt } from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { CurrencyDisplay } from '../../components/CurrencyDisplay';
import { StatCard } from '../../components/StatCard';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { formatDate, formatCurrency, formatHours } from '../../utils/format';
import { usePayStubDetail } from '../../hooks/usePayStubs';

interface StubEarning {
  type: string;
  description: string;
  hours?: number;
  rate?: number;
  amount: number;
  ytdAmount: number;
}

interface StubTax {
  type: string;
  description: string;
  amount: number;
  ytdAmount: number;
  isEmployer: boolean;
}

interface StubDeduction {
  type: string;
  description: string;
  amount: number;
  ytdAmount: number;
  preTax: boolean;
}

interface StubDetail {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  payPeriod: {
    id: string;
    startDate: string;
    endDate: string;
    payDate: string;
  };
  earnings: StubEarning[];
  taxes: StubTax[];
  deductions: StubDeduction[];
  grossPay: number;
  netPay: number;
  totalTaxes: number;
  totalDeductions: number;
  totalEmployerTaxes: number;
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
  ytdGross: number;
  ytdNet: number;
  ytdTaxes: number;
  ytdDeductions: number;
}

export function PayStubDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data: queryDataRaw, loading: isLoading } = usePayStubDetail(id || '');
  const queryData = queryDataRaw as any;
  const stub = queryData?.payStub as StubDetail | undefined;

  if (isLoading) {
    return <LoadingOverlay message="Loading pay stub..." />;
  }

  if (!stub) {
    return (
      <Stack>
        <PageHeader
          title="Pay Stub Detail"
          breadcrumbs={[
            { label: 'Payroll', to: '/admin/workforce/payroll' },
            { label: 'Pay Stubs', to: '/admin/workforce/payroll/pay-stubs' },
            { label: 'Not Found' },
          ]}
        />
        <Text c="dimmed">Pay stub not found.</Text>
      </Stack>
    );
  }

  const employeeTaxes = stub.taxes.filter((t) => !t.isEmployer);
  const employerTaxes = stub.taxes.filter((t) => t.isEmployer);

  return (
    <Stack>
      <PageHeader
        title="Pay Stub Detail"
        breadcrumbs={[
          { label: 'Payroll', to: '/admin/workforce/payroll' },
          { label: 'Pay Stubs', to: '/admin/workforce/payroll/pay-stubs' },
          { label: stub.employeeName },
        ]}
        actions={
          <Button
            icon={<IconDownload size={16} />}
            variant="light"
            component="a"
            href={`/api/workforce/pay-stubs/${stub.id}/pdf`}
            target="_blank"
          >
            Download PDF
          </Button>
        }
      />

      {/* Summary Card */}
      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" ta="flex-start">
          <div>
            <Text fw={700} size="xl">
              {stub.employeeName}
            </Text>
            <Text size="sm" c="dimmed">
              Employee #{stub.employeeNumber}
            </Text>
          </div>
          <div style={{ textAlign: 'right' }}>
            <Text size="sm" c="dimmed">
              Pay Period
            </Text>
            <Text fw={500}>
              {formatDate(stub.payPeriod.startDate)} &ndash;{' '}
              {formatDate(stub.payPeriod.endDate)}
            </Text>
            <Text size="sm" c="dimmed" mt={4}>
              Pay Date: {formatDate(stub.payPeriod.payDate)}
            </Text>
          </div>
        </Group>
        <Divider my="md" />
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          <StatCard
            title="Gross Pay"
            value={formatCurrency(stub.grossPay)}
            icon={<IconReceipt size={24} />}
            color="green"
          />
          <StatCard
            title="Total Taxes"
            value={formatCurrency(stub.totalTaxes)}
            icon={<IconReceipt size={24} />}
            color="orange"
          />
          <StatCard
            title="Total Deductions"
            value={formatCurrency(stub.totalDeductions)}
            icon={<IconReceipt size={24} />}
            color="yellow"
          />
          <StatCard
            title="Net Pay"
            value={formatCurrency(stub.netPay)}
            icon={<IconReceipt size={24} />}
            color="blue"
          />
        </SimpleGrid>
      </Paper>

      {/* Earnings */}
      <Paper withBorder p="lg" radius="md">
        <Title order={4} mb="md">
          Earnings
        </Title>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Type</Table.Th>
              <Table.Th ta="right">Hours</Table.Th>
              <Table.Th ta="right">Rate</Table.Th>
              <Table.Th ta="right">Amount</Table.Th>
              <Table.Th ta="right">YTD</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {stub.earnings.map((e, i) => (
              <Table.Tr key={i}>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {e.description || e.type}
                  </Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm">
                    {e.hours != null ? formatHours(e.hours) : '\u2014'}
                  </Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm">
                    {e.rate != null ? formatCurrency(e.rate) : '\u2014'}
                  </Text>
                </Table.Td>
                <Table.Td ta="right">
                  <CurrencyDisplay amount={e.amount} size="sm" />
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm" c="dimmed">
                    {formatCurrency(e.ytdAmount)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
            <Table.Tr>
              <Table.Td colSpan={3}>
                <Text fw={700} size="sm">
                  Total Earnings
                </Text>
              </Table.Td>
              <Table.Td ta="right">
                <CurrencyDisplay amount={stub.grossPay} size="sm" />
              </Table.Td>
              <Table.Td ta="right">
                <Text size="sm" c="dimmed" fw={600}>
                  {formatCurrency(stub.ytdGross)}
                </Text>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Taxes */}
      <Paper withBorder p="lg" radius="md">
        <Title order={4} mb="md">
          Taxes (Employee)
        </Title>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Type</Table.Th>
              <Table.Th ta="right">Amount</Table.Th>
              <Table.Th ta="right">YTD</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {employeeTaxes.map((t, i) => (
              <Table.Tr key={i}>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {t.description || t.type}
                  </Text>
                </Table.Td>
                <Table.Td ta="right">
                  <CurrencyDisplay amount={t.amount} size="sm" />
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm" c="dimmed">
                    {formatCurrency(t.ytdAmount)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
            <Table.Tr>
              <Table.Td>
                <Text fw={700} size="sm">
                  Total Employee Taxes
                </Text>
              </Table.Td>
              <Table.Td ta="right">
                <CurrencyDisplay amount={stub.totalTaxes} size="sm" />
              </Table.Td>
              <Table.Td ta="right">
                <Text size="sm" c="dimmed" fw={600}>
                  {formatCurrency(stub.ytdTaxes)}
                </Text>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>

        {employerTaxes.length > 0 && (
          <>
            <Divider my="md" />
            <Title order={5} mb="sm">
              Taxes (Employer)
            </Title>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Type</Table.Th>
                  <Table.Th ta="right">Amount</Table.Th>
                  <Table.Th ta="right">YTD</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {employerTaxes.map((t, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Group gap="xs">
                        <Text size="sm" fw={500}>
                          {t.description || t.type}
                        </Text>
                        <Badge size="xs" variant="light" color="gray">
                          Employer
                        </Badge>
                      </Group>
                    </Table.Td>
                    <Table.Td ta="right">
                      <CurrencyDisplay amount={t.amount} size="sm" />
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" c="dimmed">
                        {formatCurrency(t.ytdAmount)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
                <Table.Tr>
                  <Table.Td>
                    <Text fw={700} size="sm">
                      Total Employer Taxes
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <CurrencyDisplay
                      amount={stub.totalEmployerTaxes}
                      size="sm"
                    />
                  </Table.Td>
                  <Table.Td />
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </>
        )}
      </Paper>

      {/* Deductions */}
      <Paper withBorder p="lg" radius="md">
        <Title order={4} mb="md">
          Deductions
        </Title>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Type</Table.Th>
              <Table.Th ta="right">Amount</Table.Th>
              <Table.Th ta="right">YTD</Table.Th>
              <Table.Th>Pre-Tax</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {stub.deductions.map((d, i) => (
              <Table.Tr key={i}>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {d.description || d.type}
                  </Text>
                </Table.Td>
                <Table.Td ta="right">
                  <CurrencyDisplay amount={d.amount} size="sm" />
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm" c="dimmed">
                    {formatCurrency(d.ytdAmount)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {d.preTax ? (
                    <Badge size="xs" color="green" variant="light">
                      Pre-Tax
                    </Badge>
                  ) : (
                    <Badge size="xs" color="gray" variant="light">
                      Post-Tax
                    </Badge>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
            <Table.Tr>
              <Table.Td>
                <Text fw={700} size="sm">
                  Total Deductions
                </Text>
              </Table.Td>
              <Table.Td ta="right">
                <CurrencyDisplay amount={stub.totalDeductions} size="sm" />
              </Table.Td>
              <Table.Td ta="right">
                <Text size="sm" c="dimmed" fw={600}>
                  {formatCurrency(stub.ytdDeductions)}
                </Text>
              </Table.Td>
              <Table.Td />
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Paper>

      {/* YTD Summary */}
      <Paper withBorder p="lg" radius="md">
        <Title order={4} mb="md">
          Year-to-Date Summary
        </Title>
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
              YTD Gross
            </Text>
            <Text fw={700} fz={24}>
              {formatCurrency(stub.ytdGross)}
            </Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
              YTD Taxes
            </Text>
            <Text fw={700} fz={24}>
              {formatCurrency(stub.ytdTaxes)}
            </Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
              YTD Deductions
            </Text>
            <Text fw={700} fz={24}>
              {formatCurrency(stub.ytdDeductions)}
            </Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
              YTD Net
            </Text>
            <Text fw={700} fz={24}>
              {formatCurrency(stub.ytdNet)}
            </Text>
          </div>
        </SimpleGrid>
      </Paper>
    </Stack>
  );
}
